import React, { useState, useEffect } from "react";
import {
  Sun,
  Moon,
  Monitor,
  Share2,
  Upload,
  Sparkles,
  Pencil,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { userService } from "../../services/userService";
import { getStoredToken } from "../../utils/tokenUtils";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { cn } from "../../lib/utils";
import PageSkeleton from "../../components/skeletons/PageSkeleton";

const COLOUR_MODES = [
  { value: "LIGHT", label: "Light", icon: Sun },
  { value: "DARK", label: "Dark", icon: Moon },
  { value: "SYSTEM", label: "System", icon: Monitor },
];

const SLACK_THEMES = [
  { id: "aubergine", name: "Aubergine", color: "#4A154B" },
  { id: "clementine", name: "Clementine", color: "#C75D2C" },
  { id: "banana", name: "Banana", color: "#E9A733" },
  { id: "jade", name: "Jade", color: "#2C5530" },
  { id: "lagoon", name: "Lagoon", color: "#0E7664" },
  { id: "barbra", name: "Barbra", color: "#8B1538" },
  { id: "gray", name: "Gray", color: "#616061" },
  { id: "mood_indigo", name: "Mood Indigo", color: "#3D2C6B" },
];

const DEFAULT_CUSTOM_CONFIG = {
  systemNavigation: "#0B527B",
  selectedItems: "#7D95EB",
  presence: "#228B07",
  notifications: "#2840A9",
  windowGradient: true,
};

const AppearancePage = () => {
  const {
    theme,
    updateTheme,
    refreshTheme,
    isLoading: themeLoading,
  } = useTheme();
  const [activeSubTab, setActiveSubTab] = useState("slack");
  const [themeLoadingState, setThemeLoadingState] = useState(true);
  const [slackThemeId, setSlackThemeId] = useState(null);
  const [customConfig, setCustomConfig] = useState(DEFAULT_CUSTOM_CONFIG);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = getStoredToken();
        if (!token) {
          setThemeLoadingState(false);
          return;
        }
        const res = await userService.getTheme();
        if (res?.success && res?.data) {
          const d = res.data;
          if (d.themeType === "custom" && d.themeConfig) {
            setActiveSubTab("custom");
            setCustomConfig({
              systemNavigation:
                d.themeConfig.systemNavigation ??
                DEFAULT_CUSTOM_CONFIG.systemNavigation,
              selectedItems:
                d.themeConfig.selectedItems ??
                DEFAULT_CUSTOM_CONFIG.selectedItems,
              presence:
                d.themeConfig.presence ?? DEFAULT_CUSTOM_CONFIG.presence,
              notifications:
                d.themeConfig.notifications ??
                DEFAULT_CUSTOM_CONFIG.notifications,
              windowGradient:
                d.themeConfig.windowGradient ??
                DEFAULT_CUSTOM_CONFIG.windowGradient,
            });
          } else if (d.themeConfig?.slackThemeId) {
            setSlackThemeId(d.themeConfig.slackThemeId);
          }
        }
      } catch (e) {
        console.error("Load theme:", e);
      } finally {
        setThemeLoadingState(false);
      }
    };
    load();
  }, []);

  const handleColourModeChange = async (mode) => {
    await updateTheme(mode);
  };

  const handleSlackThemeSelect = async (themeId) => {
    setSlackThemeId(themeId);
    setSaveLoading(true);
    try {
      await userService.saveTheme({
        themeMode: theme,
        themeType: "slack",
        themeConfig: { slackThemeId: themeId },
      });
      await refreshTheme();
    } catch (e) {
      console.error("Save slack theme:", e);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCustomConfigChange = (key, value) => {
    setCustomConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveCustomTheme = async () => {
    setSaveLoading(true);
    try {
      await userService.saveTheme({
        themeMode: theme,
        themeType: "custom",
        themeConfig: {
          systemNavigation: customConfig.systemNavigation,
          selectedItems: customConfig.selectedItems,
          presence: customConfig.presence,
          notifications: customConfig.notifications,
          windowGradient: customConfig.windowGradient,
        },
      });
      await refreshTheme();
    } catch (e) {
      console.error("Save custom theme:", e);
    } finally {
      setSaveLoading(false);
    }
  };

  if (themeLoadingState) {
    return (
      <div className="min-h-[40vh]">
        <PageSkeleton />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="appearance-page">
      <h1 className="text-2xl font-semibold text-[var(--color-text-primary,#111827)] mb-2">
        Appearance
      </h1>
      <p className="text-sm text-[var(--color-text-secondary,#6b7280)] mb-6">
        Choose how the app looks for you. These settings apply only to your
        account.
      </p>

      {/* Colour mode */}
      <section className="mb-8" data-testid="colour-mode-section">
        <h2 className="text-lg font-medium text-[var(--color-text-primary)] mb-1">
          Colour mode
        </h2>
        <p className="text-sm text-[var(--color-text-secondary)] mb-3">
          Choose if the appearance should be light or dark, or follow your
          computer&apos;s settings.
        </p>
        <div className="flex gap-2 flex-wrap">
          {COLOUR_MODES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => handleColourModeChange(value)}
              disabled={themeLoading}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors",
                theme === value
                  ? "border-[var(--link-color,#6D5EF3)] bg-[var(--color-primary-100,#ede9fe)] text-[var(--link-color,#6D5EF3)]"
                  : "border-[var(--color-border-primary)] bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* Sub-tabs */}
      <div className="flex border-b border-[var(--color-border-primary)] mb-6">
        <button
          type="button"
          data-testid="tab-default-themes"
          onClick={() => setActiveSubTab("slack")}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
            activeSubTab === "slack"
              ? "border-[var(--link-color,#6D5EF3)] text-[var(--link-color,#6D5EF3)]"
              : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]",
          )}
        >
          Default themes
        </button>
        <button
          type="button"
          data-testid="tab-custom-theme"
          onClick={() => setActiveSubTab("custom")}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
            activeSubTab === "custom"
              ? "border-[var(--link-color,#6D5EF3)] text-[var(--link-color,#6D5EF3)]"
              : "border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]",
          )}
        >
          Custom theme
        </button>
      </div>

      {activeSubTab === "slack" && (
        <section data-testid="default-themes-section">
          <h3 className="text-base font-medium text-[var(--color-text-primary)] mb-3">
            Single colour
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {SLACK_THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`theme-${t.id}`}
                onClick={() => handleSlackThemeSelect(t.id)}
                disabled={saveLoading}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border text-left transition-colors",
                  slackThemeId === t.id
                    ? "border-[var(--link-color,#6D5EF3)] ring-2 ring-[var(--link-color,#6D5EF3)]/30"
                    : "border-[var(--color-border-primary)] hover:bg-[var(--color-bg-hover)]",
                )}
              >
                <span
                  className="w-8 h-8 rounded-full shrink-0 border border-[var(--color-border-primary)]"
                  style={{ backgroundColor: t.color }}
                />
                <span className="text-sm font-medium text-[var(--color-text-primary)]">
                  {t.name}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {activeSubTab === "custom" && (
        <section data-testid="custom-theme-section">
          <h3 className="text-base font-medium text-[var(--color-text-primary)] mb-3">
            Theme colours
          </h3>
          <div className="flex gap-2 mb-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Share2 className="w-4 h-4" /> Share
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Upload className="w-4 h-4" /> Import
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
            >
              <Sparkles className="w-4 h-4" /> Surprise me
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { key: "systemNavigation", label: "System navigation" },
              { key: "selectedItems", label: "Selected items" },
              { key: "presence", label: "Presence indication" },
              { key: "notifications", label: "Notifications" },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full border border-[var(--color-border-primary)] shrink-0"
                  style={{ backgroundColor: customConfig[key] }}
                />
                <div className="flex-1 min-w-0">
                  <Label className="text-sm text-[var(--color-text-primary)]">
                    {label}
                  </Label>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Input
                      type="text"
                      value={customConfig[key]}
                      onChange={(e) =>
                        handleCustomConfigChange(key, e.target.value)
                      }
                      className="font-mono text-sm h-8"
                    />
                    <input
                      type="color"
                      value={customConfig[key]}
                      onChange={(e) =>
                        handleCustomConfigChange(key, e.target.value)
                      }
                      className="w-8 h-8 rounded cursor-pointer border border-[var(--color-border-primary)]"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-4">
            <Checkbox
              id="windowGradient"
              checked={customConfig.windowGradient}
              onCheckedChange={(checked) =>
                handleCustomConfigChange("windowGradient", !!checked)
              }
            />
            <Label
              htmlFor="windowGradient"
              className="text-sm text-[var(--color-text-primary)]"
            >
              Window gradient
            </Label>
          </div>
          <Button
            onClick={handleSaveCustomTheme}
            disabled={saveLoading}
            data-testid="save-custom-theme-btn"
            className="mt-4 bg-[var(--link-color,#6D5EF3)] hover:opacity-90"
          >
            {saveLoading ? "Saving..." : "Save custom theme"}
          </Button>
        </section>
      )}
    </div>
  );
};

export default AppearancePage;
